import axios from 'axios'
import { userManager } from '../config/auth.config.js'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
const WGP_API_KEY = import.meta.env.VITE_WGP_API_KEY || ''

// Track whether a redirect is already in progress to prevent loops
let isRedirecting = false

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 15000, // 15s timeout — prevents hanging requests
})

// Inject OIDC access token and user identity headers
apiClient.interceptors.request.use(async (config) => {
    config.headers = config.headers || {}
    config.headers['x-api-key'] = WGP_API_KEY

    const user = await userManager.getUser()
    if (user?.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`
    }
    if (user?.profile?.email) {
        config.headers['X-User-Email'] = user.profile.email
    }
    return config
})

// On 401: token expired or invalid — trigger re-authentication via Okta.
// On 403: user lacks permission — do NOT sign out, just reject the request.
// Requests with config._skipAuthRedirect will NOT trigger a redirect (used during bootstrap).
apiClient.interceptors.response.use(
    (response) => {
        // Successful response means auth is working — reset redirect flag
        isRedirecting = false
        return response
    },
    async (error) => {
        const status = error.response?.status
        const config = error.config || {}

        // Skip redirect if:
        // - flagged with _skipAuthRedirect (e.g., fetchUserProfile during initial load)
        // - a redirect is already in progress (prevent loop)
        // - user is not currently authenticated (they're on a public page post-logout)
        if (status === 401 && !config._skipAuthRedirect && !isRedirecting) {
            const currentUser = await userManager.getUser()
            // Only auto-redirect if user was previously authenticated (token expired)
            // Don't redirect if user intentionally logged out (no user in storage)
            if (currentUser) {
                isRedirecting = true
                try {
                    await userManager.removeUser()
                } catch {
                    /* ignore */
                }
                try {
                    await userManager.signinRedirect({
                        state: { redirectTo: window.location.pathname + window.location.search },
                    })
                } catch {
                    isRedirecting = false
                }
            }
        }
        // 403 is a permissions issue — don't sign out, let the UI handle it
        return Promise.reject(error)
    }
)

export default apiClient

export const searchAPI = {
    unified: (query, params = {}) =>
        apiClient.get('/api/v1/search', { params: { q: query, ...params }, timeout: 30000 }),
    suggest: (query, params = {}) =>
        apiClient.get('/api/v1/search/suggest', { params: { q: query, ...params }, timeout: 12000 }),
    insights: (searchId) =>
        apiClient.get(`/api/v1/search/insights/${encodeURIComponent(searchId)}`, { timeout: 8000 }),
}

// Initiative API
export const initiativeAPI = {
    // Get all initiatives (optional filters: ?bu=<name>&status=Production)
    list: (params) => apiClient.get('/api/v1/initiatives', { params }),

    // Get a single initiative by ID
    getById: (id) => apiClient.get(`/api/v1/initiatives/${id}`),

    // Create a new initiative
    create: (payload) => apiClient.post('/api/v1/initiatives', payload),

    // Update an initiative
    update: (id, payload) => apiClient.put(`/api/v1/initiatives/${id}`, payload),

    // Delete an initiative
    delete: (id) => apiClient.delete(`/api/v1/initiatives/${id}`),

    // Search initiatives
    search: (query, params = {}) =>
        apiClient.get('/api/v1/initiatives/search', { params: { q: query, ...params } }),

    // Relevant search with AI scoring (mode=relevant)
    relevantSearch: (query, params = {}) =>
        apiClient.get('/api/v1/initiatives/search', { params: { q: query, mode: 'relevant', ...params } }),

    // Suggest tags for an initiative (AI-powered)
    suggestTags: (payload) => apiClient.post('/api/v1/initiatives/suggest-tags', payload),

    // Find similar initiatives — legacy array shape, now served by the semantic
    // duplicate-detection engine. New callers should use duplicateCheck.
    similar: (params = {}) => apiClient.get('/api/v1/initiatives/similar', { params }),
    /**
     * Semantic duplicate detection for a draft in progress. POST because the
     * body is unpublished initiative content — it must not end up in a URL,
     * an access log, or browser history.
     */
    duplicateCheck: (payload, config = {}) =>
        apiClient.post('/api/v1/initiatives/duplicate-check', payload, config),
    duplicateCheckFeedback: (payload) =>
        apiClient.post('/api/v1/initiatives/duplicate-check/feedback', payload),

    // Collaboration — nested endpoints
    listCollaborationRequests: (id) => apiClient.get(`/api/v1/initiatives/${id}/collaboration-requests`),
    requestCollaboration: (id, payload) => apiClient.post(`/api/v1/initiatives/${id}/collaboration-requests`, payload),
    setCollaborationFlags: (id, payload) => apiClient.patch(`/api/v1/initiatives/${id}/collaboration`, payload),
    removeCollaborator: (id, email) => apiClient.delete(`/api/v1/initiatives/${id}/collaborators/${encodeURIComponent(email)}`),
}

export const collaborationAPI = {
    mine: () => apiClient.get('/api/v1/collaboration/mine'),
    inbox: () => apiClient.get('/api/v1/collaboration/inbox'),
    respond: (id, payload) => apiClient.put(`/api/v1/collaboration/${id}/respond`, payload),
    withdraw: (id) => apiClient.put(`/api/v1/collaboration/${id}/withdraw`),
}

export const dashboardsAPI = {
    listBUs: (params = {}) => apiClient.get('/api/v1/dashboards/bus', { params }),
    bu: (bu, params = {}) => apiClient.get(`/api/v1/dashboards/bu/${encodeURIComponent(bu)}`, { params }),
    approver: (params = {}) => apiClient.get('/api/v1/dashboards/approver', { params }),
}

export const approvalsAPI = {
    summary: () => apiClient.get('/api/v1/approvals/summary'),
    mine: (params = {}) => apiClient.get('/api/v1/approvals/mine', { params }),
    submitted: (params = {}) => apiClient.get('/api/v1/approvals/submitted', { params }),
    list: (params = {}) => apiClient.get('/api/v1/approvals', { params }),
    approve: (id, payload = {}) => apiClient.put(`/api/v1/approvals/${encodeURIComponent(id)}/approve`, payload),
    reject: (id, payload = {}) => apiClient.put(`/api/v1/approvals/${encodeURIComponent(id)}/reject`, payload),
    requestChanges: (id, payload = {}) => apiClient.put(`/api/v1/approvals/${encodeURIComponent(id)}/request-changes`, payload),
    processDue: (payload = {}) => apiClient.post('/api/v1/approvals/process-due', payload),
}

export const organizationAPI = {
    approverMappings: {
        list: () => apiClient.get('/api/v1/organization/approver-mappings'),
        lookup: (params = {}) => apiClient.get('/api/v1/organization/approver-mappings/lookup', { params }),
        upsert: (payload) => apiClient.put('/api/v1/organization/approver-mappings', payload),
        remove: (approverMappingId) => apiClient.delete(`/api/v1/organization/approver-mappings/${encodeURIComponent(approverMappingId)}`),
    },
    businessUnits: {
        list: () => apiClient.get('/api/v1/organization/business-units'),
        create: (payload) => apiClient.post('/api/v1/organization/business-units', payload),
        update: (businessUnitId, payload) => apiClient.patch(`/api/v1/organization/business-units/${encodeURIComponent(businessUnitId)}`, payload),
        remove: (businessUnitId) => apiClient.delete(`/api/v1/organization/business-units/${encodeURIComponent(businessUnitId)}`),
    },
    subBusinessUnits: {
        create: (businessUnitId, payload) => apiClient.post(`/api/v1/organization/business-units/${encodeURIComponent(businessUnitId)}/sub-business-units`, payload),
        update: (subBusinessUnitId, payload) => apiClient.patch(`/api/v1/organization/sub-business-units/${encodeURIComponent(subBusinessUnitId)}`, payload),
        remove: (subBusinessUnitId) => apiClient.delete(`/api/v1/organization/sub-business-units/${encodeURIComponent(subBusinessUnitId)}`),
    },
}

export const scoringAPI = {
    submit: (payload) => apiClient.post('/api/v1/scoring', payload),
    listByInitiative: (initiativeId) => apiClient.get(`/api/v1/scoring/initiative/${initiativeId}`),
    // H2 2025 hardcoded awards (winner + runner-up per category)
    awardsLeaderboard: (params = {}) => apiClient.get('/api/v1/scoring/leaderboard/awards', { params }),
    // Live aggregated leaderboard for future award cycles
    leaderboard: (params = {}) => apiClient.get('/api/v1/scoring/leaderboard', { params }),
    // Scores submitted by the currently logged-in panelist
    mine: (params = {}) => apiClient.get('/api/v1/scoring/mine', { params }),
}

export const pointsAPI = {
    // Current authenticated user's points summary
    me: (params = {}) => apiClient.get('/api/v1/points/me', { params }),
    // Leaderboard with optional filters (bu, team, period, limit)
    leaderboard: (params = {}) => apiClient.get('/api/v1/points/leaderboard', { params }),
    rules: () => apiClient.get('/api/v1/points/rules'),
}

// ─── User & Role Management API ───────────────────────────────────
export const usersAPI = {
    // Current user
    me: () => apiClient.get('/api/v1/users/me'),
    updateMe: (payload) => apiClient.put('/api/v1/users/me', payload),

    // Notification preferences
    notificationPreferences: () => apiClient.get('/api/v1/users/me/notification-preferences'),
    updateNotificationPreferences: (payload) => apiClient.put('/api/v1/users/me/notification-preferences', payload),

    // Profile picture
    uploadProfilePicture: (image) => apiClient.post('/api/v1/users/me/profile-picture', { image }),
    removeProfilePicture: () => apiClient.delete('/api/v1/users/me/profile-picture'),
    getProfilePictures: (emails) => apiClient.get('/api/v1/users/profile-pictures', { params: { emails: emails.join(',') } }),

    // User management (admin / program_manager)
    list: () => apiClient.get('/api/v1/users'),
    getByEmail: (email) => apiClient.get(`/api/v1/users/${encodeURIComponent(email)}`),
    updateProfile: (email, payload) => apiClient.put(`/api/v1/users/${encodeURIComponent(email)}/profile`, payload),
    assignRoles: (email, roles) => apiClient.put(`/api/v1/users/${encodeURIComponent(email)}/roles`, { email, roles }),
    deleteUser: (email) => apiClient.delete(`/api/v1/users/${encodeURIComponent(email)}`),

    // Award evaluator management
    assignPanelist: (payload) => apiClient.post('/api/v1/users/panelists/assign', payload),
    revokePanelist: (payload) => apiClient.post('/api/v1/users/panelists/revoke', payload),
    listPanelistsByPeriod: (period) => apiClient.get(`/api/v1/users/panelists/period/${encodeURIComponent(period)}`),

    // Reference data
    roleDefinitions: () => apiClient.get('/api/v1/users/roles/definitions'),
}

// ─── Import API ───────────────────────────────────────────────────
export const importAPI = {
    // Import from MSWiki page
    importFromMSWiki: (payload) => apiClient.post('/api/v1/import/mswiki', payload, { timeout: 60000 }),

    // Validate MSWiki credentials and page access
    validateMSWiki: (payload) => apiClient.post('/api/v1/import/mswiki/validate', payload, { timeout: 15000 }),

    // Import from PDF (file upload)
    importFromPDF: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post('/api/v1/import/pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        })
    },

    // Download template for bulk initiative Excel upload
    downloadInitiativesExcelTemplate: () => apiClient.get('/api/v1/import/initiatives/excel/template', {
        responseType: 'blob',
        timeout: 30000,
    }),

    // Preview/validate bulk initiative workbook (no DB writes)
    previewInitiativesExcel: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post('/api/v1/import/initiatives/excel/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        })
    },

    confirmInitiativesExcelImport: (previewRows) => apiClient.post(
        '/api/v1/import/initiatives/excel/confirm',
        { previewRows },
        { timeout: 120000 }
    ),

    // Download template for bulk user/roster Excel upload
    downloadUsersExcelTemplate: () => apiClient.get('/api/v1/import/users/excel/template', {
        responseType: 'blob',
        timeout: 30000,
    }),

    // Preview/validate bulk user workbook (no DB writes)
    previewUsersExcel: (file, syncMode = 'upsert') => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('syncMode', syncMode)
        return apiClient.post('/api/v1/import/users/excel/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        })
    },

    confirmUsersExcelImport: (previewRows, syncMode = 'upsert') => apiClient.post(
        '/api/v1/import/users/excel/confirm',
        { previewRows, syncMode },
        { timeout: 120000 }
    ),

    // List available connectors
    listConnectors: () => apiClient.get('/api/v1/import/connectors'),
}

// ─── Stretch Projects API ──────────────────────────────────────────
export const stretchProjectsAPI = {
    list: () => apiClient.get('/api/v1/stretch-projects'),
    getById: (id) => apiClient.get(`/api/v1/stretch-projects/${id}`),
    create: (payload) => apiClient.post('/api/v1/stretch-projects', payload),
    update: (id, payload) => apiClient.put(`/api/v1/stretch-projects/${id}`, payload),
    delete: (id) => apiClient.delete(`/api/v1/stretch-projects/${id}`),
}

// ─── Support / Bug Reports ─────────────────────────────────────────
export const supportAPI = {
    reportBug: (payload) => apiClient.post('/api/v1/support/bug-reports', payload, { _skipAuthRedirect: true }),
    listBugReports: (params = {}) => apiClient.get('/api/v1/support/bug-reports', { params }),
    updateBugReport: (id, payload) => apiClient.patch(`/api/v1/support/bug-reports/${encodeURIComponent(id)}`, payload),
    // Contact messages are historical: the inquiry form was replaced by the
    // distribution list, so nothing creates them any more. The read and update
    // paths stay so the inbox can still work the ones already filed.
    listContactMessages: (params = {}) => apiClient.get('/api/v1/support/contact', { params }),
    updateContactMessage: (id, payload) => apiClient.patch(`/api/v1/support/contact/${encodeURIComponent(id)}`, payload),
    // Both queues in one pass. Bug reports come back without screenshots or
    // logs — `getBugReport` fetches those for the one being read.
    inbox: () => apiClient.get('/api/v1/support/inbox', { timeout: 30000 }),
    getBugReport: (id) => apiClient.get(`/api/v1/support/bug-reports/${encodeURIComponent(id)}`),
}
